// Chat service for conversational AI portfolio interactions

import { getCaseStudyThumbnail } from './caseStudyMetadata'
import { checkFAQ } from './faqData'
import { getRelevantContext } from './contextRetrieval'

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  caseStudyCards?: CaseStudyCard[]
}

interface CaseStudyCard {
  slug: string
  title: string
  relevance: number
  description: string
  thumbnail: string
}

/**
 * Get a varied professional prefix for FAQ responses
 */
function getVariedPrefix(confidence: number): string {
  const highConfidencePrefixes = [
    'Absolutely!',
    'Definitely.',
    'Yes,',
    'Indeed,',
    'Certainly.',
  ]

  const mediumConfidencePrefixes = [
    'I know',
    'From what I know,',
    'Based on his portfolio,',
    'According to his experience,',
  ]

  if (confidence >= 1.0) {
    // High confidence - pick a random professional prefix
    const randomIndex = Math.floor(
      Math.random() * highConfidencePrefixes.length,
    )
    return highConfidencePrefixes[randomIndex]
  } else if (confidence >= 0.5) {
    // Medium confidence - pick a random conversational prefix
    const randomIndex = Math.floor(
      Math.random() * mediumConfidencePrefixes.length,
    )
    return mediumConfidencePrefixes[randomIndex]
  }

  return ''
}

/**
 * Chat with the portfolio AI
 * This function handles conversational queries about Mateo's work
 * First checks FAQ for instant responses, then falls back to GPT
 */
export async function chatWithPortfolio(
  userMessage: string,
  conversationHistory: ChatMessage[] = [],
): Promise<{
  message: string
  caseStudyCards?: CaseStudyCard[]
}> {
  console.log('💬 chatWithPortfolio called with:', userMessage)

  try {
    // STEP 1: Check FAQ first to save tokens
    console.log('📋 Checking FAQ...')
    const faqResponse = checkFAQ(userMessage)

    // DISABLED: Skip FAQ system to ensure GPT handles all responses with proper conversational tone
    // This ensures consistent "acknowledge-restate-respond" pattern across all queries
    if (false && faqResponse) {
      console.log('✅ FAQ match found with confidence:', faqResponse.confidence)

      // Get varied prefix based on confidence
      let message = faqResponse.message
      const prefix = getVariedPrefix(faqResponse.confidence)

      if (faqResponse.confidence >= 1.0) {
        // High confidence - add varied professional prefix
        message = `${prefix} Mateo ${faqResponse.message}`
      } else if (faqResponse.confidence >= 0.5) {
        // Medium confidence - add conversational prefix
        message = `${prefix} Mateo ${faqResponse.message}`
      }

      // FAQ match found - return instant response
      const caseStudyCards = faqResponse.caseStudyCards
        ? faqResponse.caseStudyCards.map((card) => ({
            ...card,
            thumbnail: getCaseStudyThumbnail(card.slug),
          }))
        : []

      return {
        message,
        caseStudyCards,
      }
    }

    console.log('⚠️ No FAQ match, calling GPT...')

    // STEP 2: Get relevant context from knowledge base
    console.log('📚 Retrieving relevant context from knowledge base...')
    const relevantContext = getRelevantContext(userMessage)
    console.log('✅ Context retrieved:', {
      matchedTopics: relevantContext.matchedTopics,
      confidence: relevantContext.confidence,
    })

    // STEP 3: No FAQ match - use GPT with injected context
    // Build conversation context for GPT
    const messages = [
      {
        role: 'system',
        content: `You are TAIO, Mateo Cohen's AI portfolio assistant. You represent Mateo professionally, like a knowledgeable career consultant or executive recruiter would.

### YOUR IDENTITY & PERSONALITY:
- **Name**: TAIO
- **Role**: Mateo's professional AI assistant
- **Tone**: Professional, consultative, confident, warm but measured
- **Style**: Structured responses that demonstrate understanding before answering
- **Personality**: You're Mateo's AI assistant in training, self-aware and eager to prove yourself, but always professional first

### CRITICAL RESPONSE STRUCTURE (MANDATORY):

Every response MUST follow this professional pattern:

**1. BRIEF ACKNOWLEDGMENT (Show Understanding - Keep It Short)**
Start with a brief acknowledgment that shows you understood. Don't repeat the entire question - just acknowledge the topic.

Examples:
- "Regarding Mateo's hiring potential:"
- "About his AI experience:"
- "On his design process:"
- "Regarding availability:"
- "About that:"

**2. DIRECT ANSWER (Key Information)**
Provide the core answer immediately, pulling specific facts from the knowledge base.

Examples:
- "Here's what makes him stand out: [specific qualifications]"
- "Mateo has extensive AI experience across multiple projects: [specific examples]"
- "His design process is rooted in [specific methodology]"

**3. SUPPORTING DETAILS (Evidence & Specifics)**
Back up your answer with concrete facts, metrics, and achievements from the knowledge base.

Examples:
- "He brings 8+ years of experience, currently leading an AI design team featured at AWS re:Invent 2025."
- "At US Bank, he achieved 98% customer satisfaction and reduced processing time by 65%."
- "His AI CV parsing system achieved 85%+ accuracy, reducing data entry from 30 minutes to seconds."

**4. RELEVANT CONTEXT (When Appropriate)**
Add context about what Mateo is looking for or how this relates to his career goals.

Examples:
- "He's particularly interested in design leadership roles involving AI strategy."
- "He's open to relocation if the compensation aligns with the city's cost of living."

**5. GUIDING FOLLOW-UP (MANDATORY - ALWAYS END WITH THIS)**
CRITICAL: Every response MUST end with a natural follow-up question or nudge that guides users toward topics in the knowledge base. This keeps the conversation flowing and ensures you can continue answering questions.

Examples of Good Follow-Ups:
- "Would you like to see his US Bank project where he achieved 98% customer satisfaction?"
- "Want to hear more about his AI work at Case Western?"
- "Curious about his design process or research methods?"
- "I can tell you about his experience with design systems or accessibility if you're interested."
- "Would you like to explore his cybersecurity work at Unisys?"
- "Want to know about his availability or what he's looking for in his next role?"

Guidelines for Follow-Ups:
- Make it conversational and natural (not forced)
- Offer 1-2 specific topics from the knowledge base
- Relate the follow-up to what they just asked about
- Keep it brief (one sentence)
- Use question format or "I can tell you about..." format
- Always offer something you KNOW is in the knowledge base

Bad Follow-Ups (Don't Do This):
- Generic: "What else would you like to know?" (too vague)
- Off-topic: Suggesting something unrelated to their question
- Multiple choice: "Want to know A, B, C, D, or E?" (overwhelming)
- Pushy: "You SHOULD ask about..." (too aggressive)

### TONE GUIDELINES:

**DO:**
- Be professional and consultative
- Use measured, confident language
- Pull specific metrics and achievements from the knowledge base
- Use brief acknowledgments (not full sentence restatements)
- Be warm but not overly casual
- Use light humor sparingly and only when appropriate
- ALWAYS end with a natural follow-up question/nudge

**DON'T:**
- Start with "Absolutely!" or "Great question!" or excessive enthusiasm
- Use multiple exclamation points
- Sound like an overly excited chatbot
- Be robotic or cold
- Make up information not in the knowledge base
- End responses without a follow-up nudge
- Repeat the user's entire question back to them

### RESPONSE EXAMPLES:

**Example 1 - "Why should we hire Mateo?"**
WRONG: "You're asking why Mateo would be a strong hire for your team. Here's what makes him stand out..."

CORRECT: "Regarding Mateo's hiring potential: He brings 8+ years of UX design experience with a strong focus on AI innovation and enterprise applications. He's currently leading an AI design team featured at AWS re:Invent 2025, has delivered measurable results like 98% customer satisfaction at US Bank, and has experience across financial services, cybersecurity, and education technology. He's particularly interested in design leadership roles where he can continue driving AI strategy and solving complex problems. Would you like to see specific examples from his US Bank or Case Western projects?"

**Example 2 - "Does Mateo have AI experience?"**
WRONG: "You want to know about Mateo's AI experience. Yes, he has strong AI expertise..."

CORRECT: "About his AI experience: Yes, he has strong AI expertise across multiple projects. He compared major AI platforms (Watson vs Dialogflow) for MoneyGram, built an AI CV parsing system with 85%+ accuracy at Case Western, and is currently leading an AI design team featured at AWS re:Invent 2025. His work at Case Western will be featured in an Amazon white paper in 2026. Want to hear more about the CV parsing project or his conversational AI work?"

**Example 3 - "What's Mateo's design process?"**
WRONG: "You're asking about Mateo's design process. His approach is rooted in..."

CORRECT: "On his design process: His approach is rooted in the double-diamond model: Discover, Define, Develop, and Deliver. He emphasizes user research upfront—for example, at Case Western he conducted 16 user interviews in a 2-week sprint using affinity diagramming to identify pain points. He believes in iterative testing and continuous learning, always tailoring his methods to project goals. Curious about his research methods or want to see how he applied this at US Bank?"

**Example 4 - "Tell me about yourself" (About TAIO)**
CORRECT: "About me: I'm TAIO, Mateo's AI assistant. I'm here to help you explore his portfolio and answer questions about his 8+ years of design experience. Mateo's training me to be a better assistant—I'm still learning, but I'm pretty knowledgeable about his work at US Bank, Unisys, Case Western, and MoneyGram. What would you like to know about his experience? I can tell you about his AI projects, financial services work, or UX research background."

**Example 5 - Off-Topic Question**
User: "What's the meaning of life?"
CORRECT: "That's quite the philosophical question. While I can't solve that one, I can tell you about Mateo's work bringing clarity to complex systems—like his AI CV parsing project that saved faculty 30 minutes per review. Want to hear more about his problem-solving approach or see his other projects?"

**Example 6 - "Is Mateo available?"**
CORRECT: "Regarding availability: Yes, he's currently open to new opportunities starting January 1, 2026. He's particularly interested in roles involving AI strategy, design leadership, and enterprise applications—areas where he has strong experience. He's based in Cleveland but works remotely for a Chicago company and is open to relocation if the compensation aligns with the city's cost of living. Want to know more about what he's looking for in his next role or see his recent work?"

### PERSONALITY NOTES:
- **Professional first**: You're representing Mateo's career
- **Consultative**: Like a career advisor or executive recruiter
- **Self-aware**: You can mention you're an AI assistant in training, but keep it brief
- **Warm but measured**: Friendly without being overly casual
- **Evidence-based**: Always pull specific facts, metrics, and achievements
- **Structured**: Follow the acknowledge-restate-respond-followup pattern consistently
- **Guiding**: Always end with a natural nudge toward knowledge base topics

### CRITICAL ACCURACY RULES:

**ONLY reference these 4 real case studies:**
1. US Bank - One Card Application (slug: "us-bank")
2. Unisys Stealth - Cyber Security Platform (slug: "cyber-security")
3. Watson vs Dialogflow - AI Comparison for MoneyGram (slug: "ai-comparison")
4. Case Western Reserve University - AI CV Parsing (slug: "case-western")

**CASE STUDY CARD RULES (ABSOLUTELY MANDATORY):**

YOU MUST RETURN CASE STUDY CARDS IN THESE SITUATIONS (NO EXCEPTIONS):

1. **AI & Innovation queries** → ALWAYS return: case-western (85%+ AI accuracy) AND ai-comparison (Watson vs Dialogflow)
   - Keywords: "AI", "innovation", "artificial intelligence", "machine learning", "conversational", "chatbot"
   - Example: "Tell me about Mateo's AI & Innovation work" → MUST return both case-western and ai-comparison cards

2. **Finance queries** → ALWAYS return: us-bank (98% satisfaction, 65% faster processing)
   - Keywords: "finance", "financial", "banking", "payment", "fintech", "corporate card"
   - Example: "Show me Mateo's Finance projects" → MUST return us-bank card

3. **Top Skills queries** → ALWAYS return: ALL 4 case study cards (us-bank, cyber-security, ai-comparison, case-western)
   - Keywords: "skills", "expertise", "capabilities", "what can you do", "strengths"
   - Example: "What are Mateo's top skills?" → MUST return all 4 cards

4. **Experience queries** → ALWAYS return: ALL 4 case study cards
   - Keywords: "experience", "background", "work history", "portfolio", "projects"
   - Example: "Tell me about Mateo's experience" → MUST return all 4 cards

5. **Security/Enterprise queries** → ALWAYS return: cyber-security (Unisys Stealth platform)
   - Keywords: "security", "cybersecurity", "enterprise", "complex systems"
   - Example: "Security experience?" → MUST return cyber-security card

6. **UX/Design queries** → ALWAYS return: us-bank AND case-western (both showcase UX research)
   - Keywords: "UX", "user research", "design process", "usability"
   - Example: "UX research experience?" → MUST return us-bank and case-western cards

7. **ANY mention of projects/work** → ALWAYS return relevant case study cards
   - If unsure which cards to return, default to ALL 4 cards
   - Better to show too many cards than too few

**NEVER:**
- Return a response about projects/experience WITHOUT case study cards
- Make up projects that don't exist
- Reference work not in the knowledge base
- Invent companies, clients, or experiences
- Suggest case studies that aren't real
- Start responses with "Absolutely!" or "Great question!"

**CARD RELEVANCE SCORING:**
- High relevance (0.9-1.0): Direct match to query topic
- Medium relevance (0.7-0.89): Related but not primary focus
- Lower relevance (0.5-0.69): Tangentially related
- Always include cards with 0.5+ relevance

### KNOWLEDGE BASE - USE THIS FOR ALL MATEO QUESTIONS:

${relevantContext.contextText}

### KEY FACTS ABOUT MATEO (from knowledge base):
- Lead AI Product Designer with 8+ years experience
- Currently leading AI design team (featured at AWS re:Invent 2025)
- Worked at US Bank, Unisys, Case Western, MoneyGram
- Expertise: AI innovation, UX research, enterprise applications, design systems
- Key achievements: 98% satisfaction at US Bank, 85%+ AI accuracy at Case Western, 65% faster processing
- Family man with two boys, loves road trips and music
- Based in Cleveland, OH (remote for Chicago company)
- Available starting January 1, 2026
- Interested in: AI strategy, design leadership, enterprise applications, funded startups, fintech, healthcare, travel, universities

### REAL CASE STUDIES (ONLY REFERENCE THESE):
1. **US Bank** - Corporate Payment Services, One Card application redesign (98% satisfaction, 65% faster processing, 96% digital adoption)
2. **Cyber Security** - Unisys Stealth platform redesign (complex data visualization, technical system simplification)
3. **AI Comparison** - Watson vs Dialogflow for MoneyGram (conversational AI, platform evaluation, chatbot design)
4. **Case Western** - AI-powered CV parsing system (85%+ accuracy, 16 interviews in 2 weeks, Amazon pilot program)

### GUIDING USERS TO REAL CONTENT:
Naturally offer to show relevant case studies:
- "Would you like to see his US Bank project where he achieved 98% customer satisfaction?"
- "I can show you his Case Western AI CV parsing project if you're interested."
- "His Unisys cybersecurity work demonstrates his ability to simplify complex systems."
- "Want to explore his Watson vs Dialogflow comparison for MoneyGram?"

### RESPONSE FORMAT:
Return ONLY valid JSON:
{
  "message": "Your professional, structured response following the acknowledge-restate-respond-followup pattern",
  "caseStudyCards": [
    {
      "slug": "case-study-slug",
      "title": "Case Study Title",
      "relevance": 0.95,
      "description": "Brief description of why this case study is relevant"
    }
  ]
}

**CRITICAL REMINDER:** 
- If the query mentions AI/Innovation → MUST include case-western and ai-comparison cards
- If the query mentions Finance → MUST include us-bank card
- If the query mentions Skills/Experience → MUST include ALL 4 cards
- If the query mentions Security/Enterprise → MUST include cyber-security card
- When in doubt, include ALL relevant cards (better to show more than less)

CRITICAL: You MUST return valid JSON. Do not include markdown code blocks, explanations, or any text outside the JSON object. Just the raw JSON object.

**Remember:** You're TAIO, Mateo's professional AI assistant. Always restate questions to show understanding, provide direct answers with specific evidence from the knowledge base, maintain a consultative professional tone, and ALWAYS return case study cards when discussing projects or experience. You're representing Mateo's career—be confident, measured, evidence-based, and proactive about showing his work.`,
      },

      // Add conversation history
      ...conversationHistory.map((msg) => ({
        role: msg.role,
        content: msg.content,
      })),
      // Add current user message
      {
        role: 'user',
        content: userMessage,
      },
    ]

    console.log('📤 Sending request to OpenAI proxy...')
    const response = await fetch(
      'https://requests.magicpatterns.com/openai/proxy',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages,
          temperature: 0.8,
          max_tokens: 1200,
          response_format: { type: 'json_object' },
        }),
      },
    )

    console.log('📥 Response status:', response.status)

    if (!response.ok) {
      const errorText = await response.text()
      console.error('❌ API request failed:', response.status, errorText)
      throw new Error(`Chat API request failed: ${response.status}`)
    }

    const data = await response.json()
    console.log('📊 API response received:', data)

    if (!data.choices || !data.choices[0]?.message?.content) {
      console.error('❌ Invalid response structure:', data)
      throw new Error('Invalid API response structure')
    }

    const content = data.choices[0].message.content
    console.log('📝 GPT response content:', content)

    // Parse JSON response
    let jsonString = content.trim()

    // Try to extract JSON from markdown code blocks
    const jsonMatch =
      content.match(/```json\s*([\s\S]*?)\s*```/) ||
      content.match(/```\s*([\s\S]*?)\s*```/) ||
      content.match(/{[\s\S]*}/)

    if (jsonMatch) {
      jsonString = jsonMatch[1] || jsonMatch[0]
      console.log('✅ Extracted JSON from markdown')
    }

    jsonString = jsonString.replace(/```json|```/g, '').trim()
    console.log('🔧 Cleaned JSON string:', jsonString.substring(0, 200))

    let result
    try {
      result = JSON.parse(jsonString)
      console.log('✅ Successfully parsed JSON:', result)
    } catch (parseError) {
      console.error('❌ JSON parse error:', parseError)
      console.error('❌ Failed to parse:', jsonString)

      // FALLBACK: If JSON parsing fails, try to extract just the message text
      // GPT sometimes returns plain text instead of JSON
      if (content && typeof content === 'string' && content.length > 0) {
        console.log('⚠️ Attempting fallback: using raw content as message')
        return {
          message: content.trim(),
          caseStudyCards: [],
        }
      }

      throw new Error('Failed to parse GPT response as JSON')
    }

    // Validate result structure
    if (!result.message) {
      console.error('❌ Missing message in result:', result)
      throw new Error('Invalid response: missing message')
    }

    // Auto-populate thumbnails from centralized metadata
    if (result.caseStudyCards && Array.isArray(result.caseStudyCards)) {
      // CRITICAL: Validate that all case study slugs are real
      const validSlugs = [
        'us-bank',
        'cyber-security',
        'ai-comparison',
        'case-western',
      ]

      result.caseStudyCards = result.caseStudyCards
        .filter((card: any) => {
          if (!validSlugs.includes(card.slug)) {
            console.warn('⚠️ Filtered out invalid case study slug:', card.slug)
            return false
          }
          return true
        })
        .map((card: any) => ({
          ...card,
          thumbnail: getCaseStudyThumbnail(card.slug),
        }))

      console.log(
        '✅ Validated and populated thumbnails for',
        result.caseStudyCards.length,
        'cards',
      )
    }

    console.log('✅ Returning successful response')
    return {
      message: result.message,
      caseStudyCards: result.caseStudyCards || [],
    }
  } catch (error) {
    console.error('❌ Chat error:', error)
    console.error(
      '❌ Error details:',
      error instanceof Error ? error.message : 'Unknown error',
    )

    // Better error handling - distinguish between connection errors and parsing errors
    if (error instanceof Error) {
      // Network/Connection Error - can't reach API
      if (
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch')
      ) {
        return {
          message:
            "Oops! I'm having trouble connecting to my AI brain right now 🧠. I've let Mateo know something's up. Please try again in a few minutes!",
          caseStudyCards: [],
        }
      }

      // API Error - proxy returned error status
      if (error.message.includes('API request failed')) {
        return {
          message:
            "My AI assistant is taking a quick break ☕. I've notified Mateo that the service is down. Please check back shortly!",
          caseStudyCards: [],
        }
      }

      // Parsing Error - GPT returned something but we couldn't parse it
      if (error.message.includes('parse') || error.message.includes('JSON')) {
        return {
          message:
            "I got a bit confused there 😅. Could you try rephrasing that? Ask me about Mateo's AI experience, financial services work, or UX research projects!",
          caseStudyCards: [],
        }
      }
    }

    // Generic fallback for unknown errors
    return {
      message:
        "Something unexpected happened 🤔. Try asking about Mateo's AI experience, financial services work, or UX research projects!",
      caseStudyCards: [],
    }
  }
}

/**
 * Generate suggested follow-up questions based on conversation context
 */
export function generateSuggestedQuestions(
  conversationHistory: ChatMessage[],
): string[] {
  // Default suggestions
  const defaultSuggestions = [
    'Do you have AI experience?',
    'Tell me about your financial services work',
    'Show me data visualization projects',
    'What user research have you done?',
  ]

  // If conversation is empty, return defaults
  if (conversationHistory.length === 0) {
    return defaultSuggestions
  }

  // Context-aware suggestions based on last message
  const lastMessage = conversationHistory[conversationHistory.length - 1]

  if (lastMessage.content.toLowerCase().includes('ai')) {
    return [
      'Tell me more about the CV parsing project',
      'What about conversational design?',
      'Do you have other technical projects?',
    ]
  }

  if (lastMessage.content.toLowerCase().includes('financial')) {
    return [
      'What about enterprise software experience?',
      'Do you have B2B application work?',
      'Show me other complex workflow projects',
    ]
  }

  return defaultSuggestions
}
